import React, { useState, useEffect, useMemo } from 'react';
import { Environment } from 'src/Environment';
import { xLinkContacts } from 'src/common';
import TealiumEvents from 'src/helpers/tealium-events';
import { ChangePasswordForm, BankingDetailsForm } from 'src/components';
import { formatCurrencyAmountSimple } from 'src/helpers/formatting';
import HeaderNav from '../header-nav/header-nav';
import toastMessageServiceSingleton from 'src/services/toast-message.service';
import { getMerchantPrivateKey } from 'src/services/merchant-private-key.service';
import useStateLocalPersist from 'src/hooks/use-state-local-persist';
import RevampAccordion from 'src/components/revamp-accordion/revamp-accordion';
import RevampAccordionItem from 'src/components/revamp-accordion-item/revamp-accordion-item';
import { SOV_DETAILS } from 'src/helpers/constants';
import useMerchantProfile from 'src/hooks/useMerchantProfile';
import { getMerchantBankDetails } from 'src/services/merchant-profile.service';
import './merchant-profile.scss';

type BizWalletProInformation = {
  accountNumber: string;
  walletLimit: string;
  dailyLimit: string;
};

export default function MerchantProfile() {
  const { merchant, merchantBankDetails, setMerchantBankDetails } = useMerchantProfile();
  const [bizWalletDetails, setBizWalletDetails] = useState<BizWalletProInformation>();
  const [isPrivateKeyVisible, setIsPrivateKeyVisible] = useState<boolean>(false);
  const [merchantPrivateKey, setMerchantPrivateKey] = useStateLocalPersist(
    'merchant-profile-private-key',
    '',
  );
  const [isPrivateKeyLoading, setIsPrivateKeyLoading] = useState<boolean>(false);
  const [isBankDetailsLoading, setIsBankDetailsLoading] = useState<boolean>(true);
  const [pendingUpdate, setPendingUpdate] = useState<boolean>(false);

  const PENDING_BANK_UPDATE = 'Pending Updates';

  const updateBankingDetailsEnabled = useMemo(
    (): boolean => !Environment.current?.disabledFeatures?.updateBankingDetails,
    [],
  );

  const initializePrivateKey = async (abortController: AbortController) => {
    const merchantPrivateKeyResponse = await getMerchantPrivateKey(abortController);
    if (merchantPrivateKeyResponse.ok && merchantPrivateKeyResponse.result) {
      setMerchantPrivateKey(merchantPrivateKeyResponse.result.authKey);
    }
  };

  const getMerchantDetails = async (abortController: AbortController, merchantId: string) => {
    setIsBankDetailsLoading(true);
    try {
      const response = await getMerchantBankDetails(abortController, merchantId);
      if (!response.ok) {
        toastMessageServiceSingleton.addError(response?.message, null, '', false, null, true);
        setIsBankDetailsLoading(false);
        return;
      }

      if (response.ok && !abortController.signal.aborted) {
        response.responseCode === 1001 && response.message === PENDING_BANK_UPDATE
          ? setPendingUpdate(true)
          : setMerchantBankDetails(response.data);

        setIsBankDetailsLoading(false);
      }
    } catch (error) {
      console.error(error);
      setIsBankDetailsLoading(false);
    }
  };

  useEffect(() => {
    if (!merchantPrivateKey && isPrivateKeyVisible && !isPrivateKeyLoading) {
      const abortController = new AbortController();
      setIsPrivateKeyLoading(true);
      initializePrivateKey(abortController);

      return () => {
        setIsPrivateKeyLoading(false);
        abortController.abort();
      };
    }
  }, [merchantPrivateKey, isPrivateKeyVisible]);

  useEffect(() => {
    TealiumEvents.merchantProfilePageLoadNewStyle();

    if (window.innerWidth > 769) {
      toastMessageServiceSingleton.addToast({
        message: (
          <span className="change-details-toast-message">
            Contact the call centre to change details on your profile:{' '}
            <a href={xLinkContacts.numberUrl}>
              <b>{xLinkContacts.number}</b>
            </a>
          </span>
        ),
        permanent: true,
      });

      if (!Environment.current?.disabledFeatures?.sovDashboard) {
        setBizWalletDetails(SOV_DETAILS.BizWalletProInformation);
      }

      return () => {
        toastMessageServiceSingleton.removeAllToasts();
      };
    }
  }, []);

  useEffect(() => {
    if (merchant.merchantId && updateBankingDetailsEnabled) {
      const abortController = new AbortController();
      getMerchantDetails(abortController, merchant.merchantId);
      return () => {
        abortController.abort();
      };
    }
  }, [merchant]);

  const getFormattedPrivateKey = (privateKey: string, isVisible: boolean) => {
    if (privateKey) {
      return isVisible ? privateKey : privateKey.replace(/./g, '*');
    } else {
      return '';
    }
  };

  return (
    <div className="MerchantProfile" data-testid="MerchantProfile">
      <HeaderNav
        title={merchant?.companyName ? `Hi, welcome back ${merchant?.companyName}` : 'Loading...'}
        screenName="My Profile"
      >
        <RevampAccordion>
          <RevampAccordionItem title="Profile Details">
            <section className="flex flex-col w-full">
              <h3 className="text-white-50">Merchant ID</h3>
              <p className="text-grey-400">{merchant?.merchantId ?? 'Loading...'}</p>
            </section>
            <section className="flex flex-col w-full">
              <h3 className="text-white-50">Merchant type</h3>
              <p className="text-grey-400">{merchant?.merchantType ?? 'Loading...'}</p>
            </section>
            <section className="flex flex-col w-full">
              <h3 className="text-white-50">MCC</h3>
              <p className="text-grey-400">{merchant?.mccCode ?? 'Loading...'}</p>
            </section>
            <section className="flex flex-col w-full">
              <h3 className="text-white-50">Company name</h3>
              <p className="break-words text-grey-400">{merchant?.companyName ?? 'Loading...'}</p>
            </section>
            <section className="flex flex-col w-full">
              <h3 className="text-white-50">Company reg number</h3>
              <p className="text-grey-400">{merchant?.companyRegistrationNumber ?? 'Loading...'}</p>
            </section>
            <section className="flex flex-col w-full">
              <h3 className="text-white-50">MCC description</h3>
              <p className="text-grey-400">{merchant?.mccDescription ?? 'Loading...'}</p>
            </section>
          </RevampAccordionItem>

          {updateBankingDetailsEnabled ? (
            <>
              <RevampAccordionItem title="Contact Details">
                <section className="flex flex-col w-full">
                  <h3 className="text-white-50">Contact person</h3>
                  <p className="text-grey-400">
                    {merchantBankDetails?.contactPerson ?? 'Loading...'}
                  </p>
                </section>
                <section className="flex flex-col w-full">
                  <h3 className="text-white-50">Contact email address</h3>
                  <p className="text-grey-400">
                    {merchantBankDetails?.emailAddress ?? 'Loading...'}
                  </p>
                </section>
                <section className="flex flex-col w-full">
                  <h3 className="text-white-50">Contact number</h3>
                  <p className="text-grey-400">
                    {merchantBankDetails?.mobilePhoneNumber ?? 'Loading...'}
                  </p>
                </section>
              </RevampAccordionItem>
              <RevampAccordionItem
                title="Banking Details"
                onExpand={() => TealiumEvents.myProfileExpandsOrCollapsesTab()}
              >
                <BankingDetailsForm
                  isBankDetailsLoading={isBankDetailsLoading}
                  pendingUpdate={pendingUpdate}
                  getMerchantDetails={getMerchantDetails}
                />
              </RevampAccordionItem>
            </>
          ) : (
            <></>
          )}
          {!Environment.current?.disabledFeatures?.sovDashboard ? (
            <RevampAccordionItem title="BizWallet Pro information">
              <section className="flex flex-col w-full">
                <h3 className="text-white-50">Account number</h3>
                <p className="text-grey-400">{bizWalletDetails?.accountNumber ?? 'Loading...'}</p>
              </section>
              <section className="flex flex-col w-full">
                <h3 className="text-white-50">Wallet limit</h3>
                <p className="text-grey-400">
                  {`R ${formatCurrencyAmountSimple(bizWalletDetails?.walletLimit)}` ?? 'Loading...'}
                </p>
              </section>
              <section className="flex flex-col w-full">
                <h3 className="text-white-50">Daily cash withdrawal limit</h3>
                <p className="text-grey-400">
                  {`R ${formatCurrencyAmountSimple(bizWalletDetails?.dailyLimit)}` ?? 'Loading...'}
                </p>
              </section>
            </RevampAccordionItem>
          ) : (
            <></>
          )}
          <RevampAccordionItem title="e-Commerce">
            <section className="flex flex-wrap items-center justify-between">
              <h3>Payment gateway private key</h3>
              <div className="flex items-center gap-x-2 w-full sm:w-[unset]">
                <p className="w-full sm:w-[unset] text-grey-400 text-xs sm:text-base">
                  {isPrivateKeyLoading
                    ? 'Loading...'
                    : getFormattedPrivateKey(merchantPrivateKey, isPrivateKeyVisible)}
                </p>

                <i
                  className={`cursor-pointer text-xl ${
                    isPrivateKeyVisible ? 'icon-eye-hide' : 'icon-eye-show'
                  }`}
                  onClick={() => {
                    setIsPrivateKeyVisible((isVisible) => !isVisible);
                    TealiumEvents.showPrivateKey();
                  }}
                />
              </div>
            </section>
          </RevampAccordionItem>
          <RevampAccordionItem title="Change password">
            <ChangePasswordForm />
          </RevampAccordionItem>
        </RevampAccordion>
      </HeaderNav>
    </div>
  );
}
